import { FC, useState, useEffect } from "react";
import { Dictionary } from "../../Helpers";
import { ValidatedInputProps } from "./Common";
import { useForceUpdate } from "../../Hooks";
import commonStyles from "./Common.module.scss";
import styles from "./Tags.module.scss";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { classNames } from "../../Utilities";
import { Autocomplete } from "./Autocomplete";

interface LookupTagsProps {
    id: string;
    label: string;
    initialItems?: Dictionary<string>;
    onAutocomplete?: (value: string) => Promise<Dictionary<string>>;
    value?: string[];
    onChange?: (value: string[]) => void;
}

export const LookupTags: FC<LookupTagsProps & ValidatedInputProps> = (props) => {
    const [knownItems, setKnownItems] = useState<Dictionary<string>>(props.initialItems || {});

    const forceUpdate = useForceUpdate();

    useEffect(() => {
        props.validator?.register({
            key: props.id,
            validations: props.validations,
            value: props.value,
            forceUpdate: forceUpdate,
            validationMessages: props.customMessages
        });

        return () => {
            props.validator?.unregister(props.id);
        };
    }, []);

    useEffect(() => {
        if (props.validator) {
            props.validator.setValue(props.id, props.value ?? null);
            forceUpdate();
        }
    }, [props.value]);

    const handleItemSelected = (key: string) => {
        props.onChange?.([...(props.value ?? []), key]);
    };

    const isErrorState = (): boolean => {
        return !!props.validator && props.validator.submitAttempted() && props.validator.getErrors(props.id).length > 0;
    };

    const handleAutocomplete = async (value: string): Promise<string[]> => {
        if (!props.onAutocomplete) return [];

        const items = await props.onAutocomplete(value);
        setKnownItems({ ...knownItems, ...items });
        return Object.keys(items);
    };

    return (
        <div className={commonStyles.container}>
            {!!props.label && (
                <div className={classNames([commonStyles.label, isErrorState() && commonStyles.error])}>
                    <label htmlFor={props.id}>{props.label}</label>
                </div>
            )}

            {!!props.value?.length && (
                <div className={styles.tags}>
                    {props.value.map((key) => {
                        return (
                            <div key={key} className={styles.tag}>
                                <span>{knownItems[key]}</span>
                                {!!props.onChange && (
                                    <button
                                        type="button"
                                        className="asLink"
                                        onClick={() => props.onChange?.(props.value?.filter(f => f !== key) ?? [])}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!!props.onChange && !!props.onAutocomplete && (
                <Autocomplete
                    id={`${props.id}-autocomplete`}
                    onAutocomplete={handleAutocomplete}
                    onAutocompleteSelected={handleItemSelected}
                    autocompleteOptionKey={key => key}
                    autocompleteDisplayOption={key => knownItems[key]}
                    autoClear={true}
                />
            )}
        </div>
    );
};